import { useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { loadProject, saveWorkingProjectByFields } from '@/store/slices/project';
import {
  getWorkingProject,
  selectIsSavingWorkingProject,
  selectWorkingProjectUuid,
  selectWorkingProjectLanguages,
} from '@/store/slices/project/selectors';
import {
  downloadTranslationFile,
  uploadTranslationFile,
  saveTranslationFile,
  commitTranslationFile,
  TranslationUploadResult,
} from '@/connection/translations/translations';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import Popover from '@ui/popover/popover';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import UploadLarge from '@/general-ui/uploads/upload-large';
import { generateBorderDashSvg } from '@/micros/edit-project/helpers/constants/border-constants';
import SettingsEditPanel from '../../controls/settings-edit-panel';
import PageTranslationsDetail from './page-translations-detail';
import PageTranslationsUploadModal from './page-translations-upload-modal';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';
import TranslateLangsMap from '@/utils/translate-langs-map.json';

const langsMap = TranslateLangsMap as Record<string, string>;

const PageTranslationsWidget = () => {
  const dispatch = useAppDispatch();
  const isSaving = useAppSelector(selectIsSavingWorkingProject);
  const workingProject = useAppSelector(getWorkingProject);
  const projectUuid = useAppSelector(selectWorkingProjectUuid);
  const eventLanguages = useAppSelector(selectWorkingProjectLanguages);
  const portalRef = useRef<HTMLDivElement | null>(null);

  const translationsEnabled = (workingProject?.settings?.translation_version ?? 1) >= 2;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploadResult, setUploadResult] = useState<TranslationUploadResult | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const languageOptions = useMemo(
    () => (eventLanguages ?? []).map((code) => ({ value: code, label: langsMap[code] ?? code })),
    [eventLanguages]
  );

  const selectedLabel = languageOptions.find((o) => o.value === selectedLanguage)?.label;

  const handleToggle = (_key: string, on: boolean) => {
    if (!workingProject) return;
    dispatch(
      saveWorkingProjectByFields({
        settings: { ...workingProject.settings, translation_version: on ? 2 : 1 },
      })
    );
  };

  const handleFile = (file: File | FileList) => {
    setSelectedFile(file instanceof File ? file : (file[0] ?? null));
  };

  const handleSave = async () => {
    if (!selectedFile || !selectedLanguage || !projectUuid) return;
    try {
      setUploading(true);
      const result = await uploadTranslationFile(selectedFile, projectUuid, selectedLanguage);
      setUploadResult(result);
      setModalOpen(true);
    } catch (err) {
      const status = (err as { status?: number }).status;
      toastTrigger('Failed to upload translations', {
        type: EToastTypes.error,
        autoClose: 5000,
        description:
          status === 504
            ? 'The server took too long to respond. Your file may be large — try again in a moment.'
            : 'Please try again later',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleApply = async () => {
    if (!projectUuid || !uploadResult) return;
    try {
      setApplying(true);
      await saveTranslationFile(projectUuid, uploadResult.manifests);
      await commitTranslationFile(projectUuid);
      dispatch(loadProject({ uuid: projectUuid, doNotSetLoadingState: true }));
      toastTrigger('Translations applied successfully', { type: EToastTypes.success, autoClose: 4000 });
      setModalOpen(false);
      setUploadResult(null);
      setSelectedFile(null);
      setSelectedLanguage('');
    } catch (err) {
      const status = (err as { status?: number }).status;
      toastTrigger('Failed to apply translations', {
        type: EToastTypes.error,
        autoClose: 5000,
        description:
          status === 504
            ? 'The server took too long to respond. Please try again in a moment.'
            : 'Please try again later',
      });
    } finally {
      setApplying(false);
    }
  };

  const handleReupload = () => {
    setModalOpen(false);
    setUploadResult(null);
    setSelectedFile(null);
    setSelectedLanguage('');
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setSelectedLanguage('');
  };

  const handleDownload = async () => {
    if (!projectUuid) return;
    try {
      setDownloading(true);
      const res = await downloadTranslationFile(projectUuid);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `translations_${projectUuid}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toastTrigger('Failed to download translations', {
        type: EToastTypes.error,
        autoClose: 5000,
        description: 'Please try again later',
      });
    } finally {
      setDownloading(false);
    }
  };

  const downloadButton = (
    <Button
      onClick={handleDownload}
      loading={downloading}
      disabled={!projectUuid || downloading || isSaving}
      design="LocalBttnSec"
      designSize="small"
      className="flex ai-center gap-6"
    >
      <Icon name={ICONS.DOWNLOAD} size={12}></Icon>
      <Text className="L2">Download Translations</Text>
    </Button>
  );

  return (
    <>
      <PageTranslationsUploadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        result={uploadResult}
        onApply={handleApply}
        onReupload={handleReupload}
        applying={applying}
      />
      <SettingsEditPanel
        isEditing={translationsEnabled}
        onSave={handleSave}
        onCancel={handleCancel}
        saveDisabled={uploading || !selectedFile || !selectedLanguage}
        footerLeftContent={downloadButton}
        cancelLabel="Reset"
        expandContent={
          <Container className="flex gap-16 padding-h-16">
            <Container height="32px" className="flex-2">
              <Container
                design="GlobalCont1"
                height="32px"
                borderRadius="8px"
                minWidth="630px"
                style={{
                  backgroundImage: `url(${generateBorderDashSvg('#9B97A1', 8)})`,
                }}
              >
                <UploadLarge
                  key={selectedFile?.name ?? 'empty'}
                  allowedFileTypes={['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']}
                  onFile={handleFile}
                  CustomTitle={
                    selectedFile ? (
                      <Text className="P2" design="GlobalIcoText1" clamp={1}>
                        {selectedFile.name}
                      </Text>
                    ) : (
                      <span className="flex gap-4 ai-center">
                        <Icon name={ICONS.UPLOAD_V2} size={12} />
                        <Text className="P2" design="GlobalIcoText1">
                          Upload new
                        </Text>
                        <Text className="P2" design="GlobalIcoText3">
                          (.xlsx file)
                        </Text>
                      </span>
                    )
                  }
                  uploading={uploading}
                  disabled={uploading || isSaving}
                  placeholder=""
                />
              </Container>
            </Container>

            <Popover
              closeOnClick={false}
              border="none"
              verticalOffset={4}
              width="fit-content"
              containerClassName="flex-1"
              portalEl={portalRef}
              disabled={uploading || isSaving}
              renderTriggerButton={({ open, setOpen, setClose }) => (
                <Button
                  className="clear-button width-100-percent"
                  disabled={uploading || isSaving}
                  disabledOpacity={0.6}
                  onClick={() => (open ? setClose() : setOpen())}
                >
                  <Container
                    className="flex ai-center jc-space-between padding-h-12 S3"
                    background={open ? 'var(--global-cont-2)' : 'var(--global-cont-1)'}
                    borderRadius="100px"
                    border="1px solid var(--global-ico-text-3)"
                    height="32px"
                    width="100%"
                  >
                    <Text className="S3" design={selectedLabel ? 'GlobalIcoText1' : 'GlobalIcoText3'} clamp={1}>
                      {selectedLabel ?? 'Select Language'}
                    </Text>
                    <RotatableIcon
                      name={ICONS.SELECT_DROPDOWN_CARET}
                      size={14}
                      color="var(--global-ico-text-3)"
                      rotate={open ? '180deg' : '0deg'}
                    />
                  </Container>
                </Button>
              )}
            >
              {({ triggerBounds, handleClose }) => (
                <Container
                  design="GlobalCont2"
                  borderRadius="12px"
                  className="position-fixed overflow-hidden"
                  style={{
                    top: (triggerBounds?.bottom ?? 0) + 4,
                    left: triggerBounds?.left ?? 0,
                    width: triggerBounds?.width ?? 160,
                    zIndex: 100000,
                  }}
                >
                  <Container maxHeight="200px" className="no-scrollbar overflow-y-scroll padding-v-8">
                    {languageOptions.map((opt) => (
                      <Button
                        key={opt.value}
                        className="clear-button width-100-percent text-left S3 padding-v-8 padding-h-16"
                        backgroundColor={opt.value === selectedLanguage ? 'var(--global-cont-3)' : 'transparent'}
                        hoverBackgroundColor="var(--global-cont-3)"
                        onClick={() => {
                          setSelectedLanguage(opt.value);
                          handleClose();
                        }}
                      >
                        <Text
                          className="S3"
                          design={opt.value === selectedLanguage ? 'GlobalIcoText1' : 'GlobalIcoText2'}
                        >
                          {opt.label}
                        </Text>
                      </Button>
                    ))}
                  </Container>
                </Container>
              )}
            </Popover>
          </Container>
        }
      >
        <PageTranslationsDetail
          isEditing={translationsEnabled}
          disabled={isSaving || uploading}
          onToggle={handleToggle}
        />
      </SettingsEditPanel>
    </>
  );
};

export default PageTranslationsWidget;
